import { Form } from "@unform/web";
import { useField } from "@unform/core";
import React, { useEffect, useRef, useState } from "react";
import { BiArrowBack } from "react-icons/bi";
import { IoMdCheckmark } from "react-icons/io";
import { MdCloudUpload, MdDescription } from "react-icons/md";
import { Link, useHistory } from "react-router-dom";
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import * as Yup from "yup";
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CustomButton from "../../../components/Form/Button";
import Input from "../../../components/Form/Input";
import InputSelect from "../../../components/Form/InputSelect";
import InputDate from "../../../components/Form/InputDate";
import messageTypes from "../../../constants/MessagesTypes";
import { edit, getStockMovement, save, uploadXmlNFe, saveFromXml } from "../../../services/StockMovement";
import { getProducts } from "../../../services/Product";
import DivDiagonal from "../../../components/DivDiagonal";
import {
  VisuallyHiddenInput,
  PageWrapper,
  ContentArea,
  PageHeader,
  ModernCard,
  CardBody,
  UploadCard,
  StyledTabs,
  ModernTableContainer,
  SectionTitle,
  SectionSubtitle,
  StyledFormWrapper,
  ButtonContainer,
  DescriptionTextarea
} from "./styles";

// Componente Textarea para Unform
function DescriptionTextareaField({ name, ...rest }) {
  const textareaRef = useRef(null);
  const { fieldName, defaultValue, registerField, error } = useField(name);

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: textareaRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: (ref) => {
        ref.current.value = '';
      },
    });
  }, [fieldName, registerField]);

  return (
    <>
      <DescriptionTextarea
        ref={textareaRef}
        id={fieldName}
        name={fieldName}
        defaultValue={defaultValue}
        {...rest}
      />
      {error && <span style={{ color: '#ff4444', fontSize: '12px', marginTop: '4px', display: 'block' }}>{error}</span>}
    </>
  );
}

export default function NewStockMovement({match}) {
  const { currentCompany } = useSelector((state) => state.CompanyReducer);
  const [loading, setLoading] = useState(false);
  const [stockMovement, setStockMovement] = useState(undefined);
  const [products, setProducts] = useState([]);
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' ou 'xml'
  const [xmlFile, setXmlFile] = useState(null);
  const [xmlProducts, setXmlProducts] = useState([]);
  const [processingXml, setProcessingXml] = useState(false);
  const formRef = useRef(null);
  const history = useHistory();

  const movementTypes = [
    {key: 'ENTRADA', value: 'Entrada'},
    {key: 'SAIDA', value: 'Saída'},
  ];

  // Modo de edição - sempre manual
  const isEditMode = match.params.id;

  async function handleSubmit(data) {
    try {
      if (loading) {
        return;
      }

      setLoading(true);
      formRef.current.setErrors({});
      
      const schema = Yup.object().shape({
        productId: Yup.string().required(messageTypes.REQUIRED_FIELD),
        movementType: Yup.string().required(messageTypes.REQUIRED_FIELD),
        quantity: Yup.number()
          .required(messageTypes.REQUIRED_FIELD)
          .min(1, 'Quantidade deve ser maior que zero'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const formData = new FormData();
      formData.append('productId', data.productId);
      formData.append('movementType', data.movementType);
      formData.append('quantity', data.quantity);
      
      // Adicionar companyId
      const companyId = localStorage.getItem('@current_company_id');
      if (companyId) {
        formData.append('companyId', companyId);
      }
      
      if (data.movementDate && data.movementDate !== '') {
        const [day, month, year] = data.movementDate.split('/');
        const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        formData.append('movementDate', isoDate);
      }

      if (data.description && data.description.trim() !== '') {
        formData.append('description', data.description);
      }

      const response = (stockMovement) ? await edit(stockMovement.id, formData) : await save(formData);

      if (response && response.status === 200) {
        toast.success(messageTypes.SUCCESS);
        history.push('/stock-movement');
      } else {
          toast.error(messageTypes.ERROR_SAVE);
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(messageTypes.ERROR);
    }
  }

  async function handleXmlUpload(event) {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    if (file.type !== 'text/xml' && file.type !== 'application/xml' && !file.name.endsWith('.xml')) {
      toast.error('Por favor, selecione um arquivo XML válido.');
      return;
    }

    setXmlFile(file);
    setProcessingXml(true);

    try {
      const formData = new FormData();
      formData.append('xml', file);

      const response = await uploadXmlNFe(formData);
      
      if (response && response.status === 200 && response.data && response.data.data) {
        setXmlProducts(response.data.data);
        toast.success('XML processado com sucesso!');
      } else {
        toast.error(response?.data?.message || 'Erro ao processar XML.');
        setXmlFile(null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao processar XML da nota fiscal.');
      setXmlFile(null);
    } finally {
      setProcessingXml(false);
    }
  }

  async function handleSaveFromXml() {
    if (xmlProducts.length === 0) {
      toast.warning('Nenhum produto para salvar.');
      return;
    }

    try {
      setLoading(true);

      const productsToSave = xmlProducts.map(product => ({
        name: product.name || '',
        brand: product.brand || '',
        costPrice: product.costPrice || 0,
        salePrice: product.salePrice || 0,
        quantity: product.quantity || 1,
        movementDate: product.movementDate || null,
      }));

      const response = await saveFromXml({ products: productsToSave });

      if (response && response.status === 200) {
        toast.success(`Movimentações de estoque criadas com sucesso! ${xmlProducts.length} produto(s) processado(s).`);
        history.push('/stock-movement');
      } else {
        toast.error(response?.data?.message || messageTypes.ERROR_SAVE);
      }

      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(error?.response?.data?.message || messageTypes.ERROR);
    }
  }

  function updateXmlProduct(index, field, value) {
    const updated = [...xmlProducts];
    updated[index] = { ...updated[index], [field]: value };
    setXmlProducts(updated);
  }

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await getProducts({
          page: 1,
          per_page: 1000,
        });
        
        if (response && response.data && response.data.data) {
          const productsOptions = response.data.data
            .filter(product => product && product.id && product.name)
            .map(product => ({
              key: product.id,
              value: product.brand 
                ? `${product.name} - ${product.brand}`
                : product.name
            }));
          setProducts(productsOptions);
        }
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
      }
    };

    loadProducts();

    const id = match.params.id;
    if (id) {
      setLoading(true);
      getStockMovement(id).then((data) => {
        setStockMovement(data);
      }).finally(() => setLoading(false));
    } else {
      setStockMovement(null);
    }

  }, [match.params.id, currentCompany?.id]);

  return (
    <PageWrapper>
      <DivDiagonal/>
      <ContentArea>
        {stockMovement !== undefined && (
          <>
            <PageHeader>
              <h1>
                {stockMovement ? (
                  <>
                    <i className="fas fa-edit"/> Editar movimentação de estoque
                  </>
                ) : (
                  <>
                    <i className="fas fa-plus"/> Nova movimentação de estoque
                  </>
                )}
              </h1>
            </PageHeader>
            
            <ModernCard>
              <CardBody>
                {!isEditMode && (
                  <StyledTabs>
                    <Tabs 
                      value={entryMode === 'manual' ? 0 : 1} 
                      onChange={(event, newValue) => setEntryMode(newValue === 0 ? 'manual' : 'xml')}
                      aria-label="modo de entrada de estoque"
                    >
                      <Tab 
                        icon={<i className="fas fa-keyboard" style={{ fontSize: '20px', marginRight: '8px' }} />}
                        iconPosition="start"
                        label="Entrada Manual" 
                      />
                      <Tab 
                        icon={<i className="fas fa-file-code" style={{ fontSize: '20px', marginRight: '8px' }} />}
                        iconPosition="start"
                        label="Importar Nota Fiscal (XML)" 
                      />
                    </Tabs>
                  </StyledTabs>
                )}

                {entryMode === 'manual' || isEditMode ? (
                  <StyledFormWrapper>
                    <Form ref={formRef} onSubmit={handleSubmit}>
                      <div className="row mb-4">
                      <div className="col-12 col-md-6 mt-4">
                        <InputSelect
                          type="text"
                          name="productId"
                          label={'Selecione o produto *'}
                          options={products}
                          required
                          defaultValue={stockMovement?.product?.id ?? ''}
                        />
                      </div>
                      <div className="col-12 col-md-6 mt-4">
                        <InputSelect
                          type="text"
                          name="movementType"
                          label={'Tipo de movimentação *'}
                          options={movementTypes}
                          required
                          defaultValue={stockMovement?.movementType ?? 'ENTRADA'}
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <Input
                          type="number"
                          name="quantity"
                          label={'Quantidade'}
                          required
                          defaultValue={stockMovement?.quantity ?? ''}
                          InputProps={{
                            min: 1,
                            step: 1
                          }}
                        />
                      </div>
                      <div className="col-12 col-md-6 mt-md-0 mt-4">
                        <InputDate
                          type="date"
                          name="movementDate"
                          label={'Data da movimentação'}
                          defaultValue={stockMovement?.movementDate ? stockMovement.movementDate.split(' ')[0] : ''}
                        />
                      </div>
                    </div>
                    <div className="row mt-4">
                      <div className="col-12">
                        <label style={{ 
                          display: 'block', 
                          color: '#b0b0b0', 
                          fontSize: '14px', 
                          fontWeight: '600', 
                          marginBottom: '8px' 
                        }}>
                          Descrição (opcional)
                        </label>
                        <DescriptionTextareaField
                          name="description"
                          placeholder="Informe uma descrição para esta movimentação de estoque..."
                          defaultValue={stockMovement?.description ?? ''}
                        />
                      </div>
                    </div>
                    <ButtonContainer>
                      <Link to="/stock-movement" style={{ textDecoration: 'none' }}>
                        <CustomButton
                          variant="outlined"
                          className="btn btn-secondary font-weight-bold text-capitalize mobile-button"
                          startIcon={<BiArrowBack/>}
                          style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            border: '2px solid rgba(255, 255, 255, 0.2)',
                            color: '#e0e0e0',
                            padding: '14px 28px',
                            borderRadius: '10px',
                            fontWeight: 600,
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          Voltar
                        </CustomButton>
                      </Link>
                      <CustomButton
                        className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                        loading={loading}
                        type="submit"
                        startIcon={<IoMdCheckmark/>}
                        style={{
                          background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '14px 32px',
                          borderRadius: '10px',
                          fontWeight: 600,
                          boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          if (!loading) {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                        }}
                      >
                        Salvar
                      </CustomButton>
                    </ButtonContainer>
                    </Form>
                  </StyledFormWrapper>
                ) : (
                  <div>
                    <div className="row mb-4">
                      <div className="col-12">
                        <UploadCard>
                          <h6 className="card-title">
                            <MdDescription /> Upload de XML da Nota Fiscal
                          </h6>
                          <p className="card-text">
                            Selecione o arquivo XML da nota fiscal para importar os produtos automaticamente.
                          </p>
                          <CustomButton
                            component="label"
                            variant="outlined"
                            className="btn btn-primary"
                            startIcon={<MdCloudUpload />}
                            loading={processingXml}
                            disabled={processingXml}
                            style={{
                              background: 'linear-gradient(135deg, #00d4ff 0%, #0099cc 100%)',
                              border: 'none',
                              color: '#ffffff',
                              fontWeight: 600,
                              padding: '14px 28px',
                              borderRadius: '10px',
                              boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)',
                              transition: 'all 0.3s ease',
                              cursor: processingXml ? 'not-allowed' : 'pointer'
                            }}
                            onMouseEnter={(e) => {
                              if (!processingXml) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 212, 255, 0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 212, 255, 0.3)';
                            }}
                            >
                            {xmlFile ? xmlFile.name : 'Selecionar arquivo XML'}
                            <VisuallyHiddenInput
                              type="file"
                              accept=".xml,text/xml,application/xml"
                              onChange={handleXmlUpload}
                            />
                          </CustomButton>
                          {xmlFile && (
                            <div className="mt-3" style={{
                              padding: '12px 20px',
                              background: 'rgba(0, 212, 255, 0.1)',
                              borderRadius: '10px',
                              border: '1px solid rgba(0, 212, 255, 0.3)'
                            }}>
                              <div style={{ color: '#00d4ff', fontSize: '14px', fontWeight: 600 }}>
                                <i className="fas fa-check-circle mr-2" /> Arquivo selecionado: <strong>{xmlFile.name}</strong>
                              </div>
                            </div>
                          )}
                        </UploadCard>
                      </div>
                    </div>

                    {xmlProducts.length > 0 && (
                      <>
                        <div className="row mb-3">
                          <div className="col-12">
                            <SectionTitle>
                              <i className="fas fa-boxes"/> Produtos encontrados na nota fiscal ({xmlProducts.length})
                            </SectionTitle>
                            <SectionSubtitle>
                              Revise os dados abaixo e ajuste se necessário antes de salvar.
                            </SectionSubtitle>
                          </div>
                        </div>

                        <ModernTableContainer component={Paper} className="mb-4">
                          <Table sx={{ minWidth: 650 }} size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell className="font-weight-bold">Produto</TableCell>
                                <TableCell className="font-weight-bold" align="center">Marca</TableCell>
                                <TableCell className="font-weight-bold" align="center">Quantidade</TableCell>
                                <TableCell className="font-weight-bold" align="center">Preço de Custo</TableCell>
                                <TableCell className="font-weight-bold" align="center">Preço de Venda</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {xmlProducts.map((product, index) => (
                                <TableRow key={index}>
                                  <TableCell>
                                    <input
                                      type="text"
                                      value={product.name || ''}
                                      onChange={(e) => updateXmlProduct(index, 'name', e.target.value)}
                                      placeholder="Nome do produto"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <input
                                      type="text"
                                      value={product.brand || ''}
                                      onChange={(e) => updateXmlProduct(index, 'brand', e.target.value)}
                                      placeholder="Marca"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <input
                                      type="number"
                                      value={product.quantity || 1}
                                      onChange={(e) => updateXmlProduct(index, 'quantity', parseInt(e.target.value) || 1)}
                                      min="1"
                                      step="1"
                                      style={{ width: '100px', margin: '0 auto', textAlign: 'center' }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <input
                                      type="number"
                                      value={product.costPrice || 0}
                                      onChange={(e) => updateXmlProduct(index, 'costPrice', parseFloat(e.target.value) || 0)}
                                      min="0"
                                      step="0.01"
                                      style={{ width: '120px', margin: '0 auto', textAlign: 'center' }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <input
                                      type="number"
                                      value={product.salePrice || 0}
                                      onChange={(e) => updateXmlProduct(index, 'salePrice', parseFloat(e.target.value) || 0)}
                                      min="0"
                                      step="0.01"
                                      style={{ width: '120px', margin: '0 auto', textAlign: 'center' }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ModernTableContainer>

                        <ButtonContainer className="teste">
                          <Link to="/stock-movement" style={{ textDecoration: 'none' }}>
                            <CustomButton
                              variant="outlined"
                              className="btn btn-secondary font-weight-bold text-capitalize mobile-button"
                              startIcon={<BiArrowBack/>}
                              style={{
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '2px solid rgba(255, 255, 255, 0.2)',
                                color: '#e0e0e0',
                                padding: '14px 28px',
                                borderRadius: '10px',
                                fontWeight: 600,
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                                e.currentTarget.style.transform = 'translateY(-2px)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                e.currentTarget.style.transform = 'translateY(0)';
                              }}
                            >
                              Voltar
                            </CustomButton>
                          </Link>
                          <CustomButton
                            className="btn btn-success font-weight-bold text-capitalize mobile-button"
                            loading={loading}
                            onClick={handleSaveFromXml}
                            startIcon={<IoMdCheckmark/>}
                            style={{
                              background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '14px 32px',
                              borderRadius: '10px',
                              fontWeight: 600,
                              boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                              transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                              if (!loading) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                            }}
                          >
                            Salvar {xmlProducts.length} Produto(s)
                          </CustomButton>
                        </ButtonContainer>
                      </>
                    )}
                  </div>
                )}
              </CardBody>
            </ModernCard>
          </>
        )}
      </ContentArea>
    </PageWrapper>
  );
}
